import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { HandCoins, CheckCircle2, XCircle, User as UserIcon, ArrowRight } from "lucide-react";

import { useTranslation } from "react-i18next";
const rupee = (v) => "Rs. " + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

/**
 * Manager Home card — shows CSA-initiated cash handovers awaiting confirmation.
 * One-tap "Mark received" adds the row to the Cashbook.
 * Renders NOTHING when there are no pending handovers.
 */
export default function ManagerPendingHandoversCard() {
  const { t: __t } = useTranslation();
  const [pending, setPending] = useState({ items: [], count: 0, total: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/manager/cash-handovers/pending");
      setPending(data || { items: [], count: 0, total: 0 });
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (id, action, reason) => {
    try {
      if (action === "approve") {
        await api.post(`/manager/cash-handover/${id}/approve`);
        toast.success(__t("components.managerpendinghandoverscard.handover_confirmed_added_to_cashbook"));
      } else {
        await api.post(`/manager/cash-handover/${id}/reject`, { reason: reason || "" });
        toast.success(__t("components.managerpendinghandoverscard.handover_rejected"));
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!pending.count) return null;

  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-4 shadow-sm dark:border-amber-900/60 dark:from-amber-950/40 dark:to-transparent"
         data-testid="manager-pending-handovers">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/20">
            <HandCoins className="h-4 w-4 text-amber-700 dark:text-amber-300" />
          </div>
          <div>
            <div className="font-display text-base font-semibold text-amber-900 dark:text-amber-100">
              Cash handovers pending · {pending.count}
            </div>
            <div className="text-xs text-amber-800 dark:text-amber-300/80">
              Salesmen handed over {rupee(pending.total)}. Confirm to add to cashbook.
            </div>
          </div>
        </div>
        <Link to="/manager/cashbook" className="flex items-center gap-1 text-xs font-semibold text-amber-700 hover:underline dark:text-amber-300" data-testid="pending-handovers-view-all">
          View in cashbook <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {pending.items.slice(0, 4).map((h) => (
          <div key={h.id} className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-card p-2 dark:border-amber-900/40"
               data-testid={`pending-handover-home-${h.id}`}>
            <div className="min-w-0 flex-1 text-xs">
              <div className="font-mono text-sm font-bold text-foreground">{rupee(h.amount)}</div>
              <div className="truncate text-[11px] text-muted-foreground">
                <UserIcon className="mr-0.5 inline h-3 w-3" />
                <b>{h.csa_name || "Salesman"}</b> · {h.shift_name || h.shift_id || "shift"} · {new Date(h.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
              {h.note && <div className="truncate text-[11px] italic text-muted-foreground">&ldquo;{h.note}&rdquo;</div>}
            </div>
            <div className="flex gap-1">
              <Button size="sm" onClick={() => act(h.id, "approve")} disabled={loading} data-testid={`home-approve-handover-${h.id}`}>
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span className="hidden md:inline">{__t("components.managerpendinghandoverscard.mark_received")}</span>
              </Button>
              <Button size="sm" variant="outline" onClick={() => {
                const r = window.prompt("Reason for rejecting (optional)?"); if (r === null) return;
                act(h.id, "reject", r);
              }} disabled={loading} data-testid={`home-reject-handover-${h.id}`}>
                <XCircle className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      {pending.count > 4 && (
        <div className="mt-2 text-center text-[11px] text-muted-foreground">
          +{pending.count - 4} more · view in cashbook
        </div>
      )}
    </div>
  );
}
